import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import type {
  AgentContextUsageView,
  AgentRestoredInterjectionView,
  AgentMessageView,
  AgentSessionHistory,
  AgentPendingReplyView,
  AgentStreamPayload,
  RealtimeEventEnvelope,
  ThinkingEffort,
  ComponentPreviewElementReference,
  AgentCapabilityMention,
} from 'shared';
import { agentApi } from '@/api/agent.api';
import { useDesktopStore } from './desktop.store';
import { useCollaborationSocket } from '@/composables/useCollaborationSocket';
import { capabilityMessageContent } from '@/utils/agent-capability-mention';

export type AgentConversationMessage = AgentMessageView & {
  pending?: boolean;
  streaming?: boolean;
};

/** Agent 会话的唯一前端真源：数据库历史、Redis 快照与 WebSocket 增量在此合并。 */
export const useAgentSessionStore = defineStore('agent-session', () => {
  const workspaceId = ref<string | null>(null);
  const sessionId = ref<string | null>(null);
  const agentId = ref<string | null>(null);
  const messages = ref<AgentMessageView[]>([]);
  const loadingHistory = ref(false);
  const suggestedRepliesMessageId = ref<string | null>(null);
  const pendingReply = ref<AgentPendingReplyView | null>(null);
  const streamingContent = ref<string | null>(null);
  let streamingRoundId: string | undefined;
  let awaitingContentRound = false;
  const finishingRunId = ref<string | null>(null);
  const replySyncError = ref('');
  const displayedPendingReply = computed({
    get: () =>
      pendingReply.value && {
        ...pendingReply.value,
        content: streamingContent.value ?? pendingReply.value.content,
      },
    set: (value: AgentPendingReplyView | null) => {
      pendingReply.value = value;
      streamingContent.value = null;
      streamingRoundId = undefined;
      awaitingContentRound = false;
    },
  });
  const conversationMessages = computed<AgentConversationMessage[]>(() => {
    const pending = displayedPendingReply.value;
    if (
      !pending ||
      messages.value.some(
        (message) => message.sender === 'agent' && message.runId === pending.runId,
      )
    )
      return messages.value;
    return [
      ...messages.value,
      {
        _id: `pending-${pending.runId}`,
        workspaceId: pending.workspaceId,
        sessionId: pending.sessionId,
        runId: pending.runId,
        sender: 'agent',
        deliveryStatus: 'accepted',
        visibleAt: pending.startedAt,
        content: pending.content,
        attachments: [],
        thinkingEnabled: null,
        thinkingEffort: null,
        thinking: pending.thinking,
        usage: null,
        evidence: [
          ...pending.toolCalls.map((tool) => ({ type: 'tool', ...tool })),
          ...(pending.progress ?? []).map((content) => ({ type: 'progress', content })),
        ],
        createdAt: pending.startedAt,
        updatedAt: pending.updatedAt,
        pending: true,
        streaming: finishingRunId.value !== pending.runId,
      },
    ];
  });
  const pendingInterjections = ref<AgentMessageView[]>([]);
  const mentionCapabilities = ref<AgentCapabilityMention[]>([]);
  watch(
    [
      workspaceId,
      agentId,
      () =>
        [...messages.value, ...pendingInterjections.value].some(
          (item) => item.sender === 'user' && /\$(?:skill|mcp):/.test(item.content),
        ),
    ],
    async ([workspace, agent, hasMentions], _previous, onCleanup) => {
      mentionCapabilities.value = [];
      if (!workspace || !agent || !hasMentions) return;
      let stale = false;
      onCleanup(() => {
        stale = true;
      });
      try {
        const capabilities = await agentApi.listMentionCapabilities(workspace, agent);
        if (!stale) mentionCapabilities.value = capabilities;
      } catch {
        // 目录暂不可用时仍显示引用自带的名称，保留原始消息供重试与重置。
      }
    },
  );
  function userMessageContent(content: string, markdown = false) {
    return capabilityMessageContent(content, mentionCapabilities.value, markdown);
  }
  const resettableMessageIds = ref<string[]>([]);
  const stoppingRunId = ref<string | null>(null);
  const lastCompression = ref<{ summary?: string } | null>(null);
  const contextUsage = ref<AgentContextUsageView | null>(null);
  let stopRealtime: (() => void) | null = null;
  let stopReconnectWatch: (() => void) | null = null;
  let refreshVersion = 0;

  function normalizePendingReply(
    snapshot: AgentPendingReplyView | null | undefined,
  ): AgentPendingReplyView | null {
    if (!snapshot) return null;
    return {
      ...snapshot,
      content: snapshot.content ?? '',
      thinking: snapshot.thinking ?? '',
      progress: Array.isArray(snapshot.progress)
        ? snapshot.progress.filter((content) => typeof content === 'string')
        : [],
      toolCalls: Array.isArray(snapshot.toolCalls) ? snapshot.toolCalls : [],
    };
  }

  async function open(
    nextWorkspaceId: string,
    nextSessionId: string,
    ownerAgentId: string | null = null,
  ): Promise<void> {
    if (workspaceId.value === nextWorkspaceId && sessionId.value === nextSessionId) return;
    close();
    workspaceId.value = nextWorkspaceId;
    sessionId.value = nextSessionId;
    agentId.value = ownerAgentId;
    const socket = useCollaborationSocket();
    void socket.joinAgentSession(nextWorkspaceId, nextSessionId).catch(() => undefined);
    stopRealtime = socket.onRealtimeEvent(handleRealtime);
    stopReconnectWatch = watch(socket.connected, (connected, wasConnected) => {
      // token/工具过程事件不进入 Outbox；重连只能用数据库 + Redis 快照恢复。
      if (connected && wasConnected === false) void refresh();
    });
    await refresh();
  }

  async function refresh(): Promise<void> {
    if (!workspaceId.value || !sessionId.value) return;
    const version = ++refreshVersion;
    const requestedWorkspaceId = workspaceId.value;
    const requestedSessionId = sessionId.value;
    const pendingAtStart = pendingReply.value;
    let history: AgentSessionHistory;
    loadingHistory.value = true;
    try {
      history = await agentApi.getSessionHistory(requestedWorkspaceId, requestedSessionId);
    } finally {
      if (version === refreshVersion) loadingHistory.value = false;
    }
    if (
      version !== refreshVersion ||
      workspaceId.value !== requestedWorkspaceId ||
      sessionId.value !== requestedSessionId
    )
      return;
    replySyncError.value = '';
    // 兼容后端滚动发布期间的旧快照：缺少可选运行态时按空集合处理，避免渲染层读取 length 失败。
    messages.value = Array.isArray(history.messages) ? history.messages : [];
    suggestedRepliesMessageId.value = history.suggestedRepliesMessageId ?? null;
    pendingInterjections.value = Array.isArray(history.pendingInterjections)
      ? history.pendingInterjections
      : [];
    resettableMessageIds.value = Array.isArray(history.resettableMessageIds)
      ? history.resettableMessageIds
      : [];
    contextUsage.value = history.contextUsage ?? null;
    if (
      stoppingRunId.value &&
      history.pendingReply?.runId !== stoppingRunId.value &&
      messages.value.some((message) => message.sender === 'agent' && message.runId === stoppingRunId.value)
    )
      stoppingRunId.value = null;
    const snapshot = normalizePendingReply(history.pendingReply);
    if (
      pendingReply.value &&
      messages.value.some(
        (message) => message.sender === 'agent' && message.runId === pendingReply.value?.runId,
      )
    ) {
      pendingReply.value = null;
      streamingContent.value = null;
      finishingRunId.value = null;
      // 正式消息与流式占位在同一轮更新，正文节点按 runId 原位复用。
      if (
        !snapshot ||
        messages.value.some(
          (message) => message.sender === 'agent' && message.runId === snapshot.runId,
        )
      )
        return;
    }
    // 快照短暂缺失不能抹掉还未落库的回复，终态消息或 completed 事件负责收口。
    if (
      !snapshot &&
      pendingReply.value &&
      finishingRunId.value !== pendingReply.value.runId &&
      !messages.value.some((message) => message.sender === 'agent' && message.runId === pendingReply.value?.runId)
    )
      return;
    // 请求期间回复已切换或收到新增量时，旧响应不能清空或覆盖新 Run。
    if (
      pendingReply.value &&
      pendingReply.value !== pendingAtStart &&
      snapshot?.runId !== pendingReply.value.runId
    )
      return;
    if (
      !snapshot ||
      !pendingReply.value ||
      snapshot.runId !== pendingReply.value.runId ||
      snapshot.revision >= pendingReply.value.revision
    ) {
      if (
        snapshot?.runId !== pendingReply.value?.runId ||
        snapshot?.content !== pendingReply.value?.content
      )
        streamingContent.value = null;
      pendingReply.value = snapshot;
      if (!snapshot) finishingRunId.value = null;
    }
  }

  async function send(
    content: string,
    attachments: File[] = [],
    resetFromMessageId?: string,
    thinkingEnabled?: boolean,
    thinkingEffort?: ThinkingEffort,
    previewElement?: ComponentPreviewElementReference,
  ): Promise<void> {
    if (!workspaceId.value || !sessionId.value) throw new Error('未打开 Agent 会话');
    const sentWorkspaceId = workspaceId.value;
    const sentSessionId = sessionId.value;
    await agentApi.sendMessage(sentWorkspaceId, sentSessionId, content, attachments, {
      previewElement,
      desktopTaskTarget: useDesktopStore().taskCreationTarget,
      resetFromMessageId,
      thinkingEnabled,
      thinkingEffort,
    });
    acknowledgeSentMessage(sentSessionId);
    if (workspaceId.value === sentWorkspaceId && sessionId.value === sentSessionId)
      await refresh().catch(() => undefined);
  }

  function acknowledgeSentMessage(sentSessionId: string): void {
    if (sessionId.value !== sentSessionId) return;
    ++refreshVersion;
    suggestedRepliesMessageId.value = null;
  }

  async function sendBrowserAnnotation(
    input: import('shared').BrowserAnnotationMessage,
  ): Promise<import('shared').BrowserAnnotationReceipt> {
    const result = await agentApi
      .sendMessage(input.workspaceId, input.sessionId, input.content, [], {
        previewElement: input.previewElement,
        desktopTaskTarget: input.desktopTaskTarget,
      })
      .catch((cause: unknown) => {
        const error = cause as { status?: number; code?: string } | null;
        if (error?.status === 0 || (error?.status ?? 0) >= 500 || error?.code === 'SESSION_CHANGED')
          throw new Error('批注发送结果未确认，请查看来源会话，勿重复发送');
        throw cause;
      });
    if (!result.previewElementAccepted)
      throw new Error('批注附件接收未确认，请查看来源会话；请先更新配套服务，勿重复发送');
    if (workspaceId.value === input.workspaceId && sessionId.value === input.sessionId) {
      acknowledgeSentMessage(input.sessionId);
      await refresh().catch(() => undefined);
    }
    return result;
  }

  async function withdrawPendingInterjection(messageId: string): Promise<boolean> {
    if (!workspaceId.value || !sessionId.value) throw new Error('未打开 Agent 会话');
    const result = await agentApi.withdrawPendingInterjection(
      workspaceId.value,
      sessionId.value,
      messageId,
    );
    await refresh();
    return result.removed;
  }

  /**
   * 取消当前正在流式输出的 Run。取消确认前保留 Redis 快照，
   * 由后端完成中断、部分内容落库并推送 completed 事件后再清空界面。
   */
  async function cancelActiveRun(): Promise<AgentRestoredInterjectionView[]> {
    if (!workspaceId.value || !sessionId.value || !pendingReply.value || stoppingRunId.value)
      return [];
    const stoppedWorkspaceId = workspaceId.value;
    const stoppedSessionId = sessionId.value;
    const stoppedRunId = pendingReply.value.runId;
    stoppingRunId.value = stoppedRunId;
    try {
      const result = await agentApi.stopConversation(
        stoppedWorkspaceId,
        stoppedSessionId,
        stoppedRunId,
      );
      if (workspaceId.value !== stoppedWorkspaceId || sessionId.value !== stoppedSessionId)
        return [];
      if (['completed', 'failed', 'cancelled', 'interrupted'].includes(result.run.status))
        finishRun(stoppedRunId);
      return result.restoredInterjections;
    } catch (error) {
      if (
        workspaceId.value === stoppedWorkspaceId &&
        sessionId.value === stoppedSessionId &&
        stoppingRunId.value === stoppedRunId
      )
        stoppingRunId.value = null;
      throw error;
    }
  }

  function finishRun(runId: string): void {
    ++refreshVersion;
    if (pendingReply.value?.runId === runId) finishingRunId.value = runId;
    if (stoppingRunId.value === runId) stoppingRunId.value = null;
    void refresh().catch(() => {
      if (pendingReply.value?.runId === runId && finishingRunId.value === runId)
        replySyncError.value = '回复已完成，消息同步失败，当前内容已保留，请重试';
    });
  }

  function close(): void {
    ++refreshVersion;
    if (sessionId.value) useCollaborationSocket().leaveAgentSession(sessionId.value);
    stopRealtime?.();
    stopRealtime = null;
    stopReconnectWatch?.();
    stopReconnectWatch = null;
    workspaceId.value = null;
    sessionId.value = null;
    agentId.value = null;
    messages.value = [];
    loadingHistory.value = false;
    suggestedRepliesMessageId.value = null;
    pendingReply.value = null;
    streamingContent.value = null;
    streamingRoundId = undefined;
    awaitingContentRound = false;
    finishingRunId.value = null;
    replySyncError.value = '';
    pendingInterjections.value = [];
    mentionCapabilities.value = [];
    resettableMessageIds.value = [];
    stoppingRunId.value = null;
    lastCompression.value = null;
    contextUsage.value = null;
  }

  function handleRealtime(event: RealtimeEventEnvelope): void {
    if (event.type === 'resource.changed.v1') {
      const payload = event.payload as {
        operation?: string;
        resourceType?: string;
        data?: { _id?: string; sessionId?: string; status?: string };
      };
      if (payload.resourceType === 'agent-message' && payload.data?.sessionId === sessionId.value)
        void refresh();
      // Run 终态通过 Outbox 重放，补足可能丢失的临时 completed 事件。
      const run = payload.data;
      if (payload.resourceType === 'agent-run' && payload.operation === 'created' &&
          run?.sessionId === sessionId.value) void refresh();
      if (
        payload.resourceType === 'agent-run' &&
        run?._id &&
        run.sessionId === sessionId.value &&
        (run._id === stoppingRunId.value || run._id === pendingReply.value?.runId) &&
        ['completed', 'failed', 'cancelled'].includes(run.status ?? '')
      )
        finishRun(run._id);
      return;
    }
    if (event.type !== 'agent.stream.v1') return;
    const stream = event.payload as unknown as AgentStreamPayload;
    if (stream.sessionId !== sessionId.value) return;
    if (stream.kind === 'started') {
      const snapshot = normalizePendingReply(stream.pendingReply);
      if (
        pendingReply.value &&
        ((pendingReply.value.runId === stream.runId && !snapshot) ||
          (snapshot?.startedAt === pendingReply.value.startedAt &&
            snapshot.revision < pendingReply.value.revision))
      )
        return;
      if (
        pendingReply.value?.runId !== stream.runId ||
        (snapshot && snapshot.content !== pendingReply.value.content)
      ) {
        streamingContent.value = null;
        streamingRoundId = undefined;
        awaitingContentRound = false;
      }
      finishingRunId.value = null;
      pendingReply.value = snapshot ?? {
        sessionId: stream.sessionId,
        runId: stream.runId,
        workspaceId: workspaceId.value ?? '',
        content: '',
        thinking: '',
        toolCalls: [],
        revision: stream.revision,
        startedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    } else if (stream.kind === 'content_delta') {
      if (
        !pendingReply.value ||
        pendingReply.value.runId !== stream.runId ||
        finishingRunId.value === stream.runId
      )
        return;
      if (
        awaitingContentRound ||
        (stream.roundId !== undefined && stream.roundId !== streamingRoundId)
      )
        streamingContent.value = null;
      streamingRoundId = stream.roundId;
      awaitingContentRound = false;
      streamingContent.value =
        (streamingContent.value ?? pendingReply.value.content) + stream.content;
    } else if (stream.kind === 'progress') {
      if (
        !pendingReply.value ||
        pendingReply.value.runId !== stream.runId ||
        stream.revision <= pendingReply.value.revision
      )
        return;
      pendingReply.value = {
        ...pendingReply.value,
        progress: [...(pendingReply.value.progress ?? []), stream.content],
        revision: stream.revision,
        updatedAt: new Date().toISOString(),
      };
      streamingContent.value = null;
    } else if (stream.kind === 'thinking') {
      if (
        !pendingReply.value ||
        pendingReply.value.runId !== stream.runId ||
        stream.revision <= pendingReply.value.revision
      )
        return;
      pendingReply.value = {
        ...pendingReply.value,
        thinking: (pendingReply.value.thinking ?? '') + stream.content,
        revision: stream.revision,
        updatedAt: new Date().toISOString(),
      };
    } else if (stream.kind === 'content') {
      if (
        !pendingReply.value ||
        pendingReply.value.runId !== stream.runId ||
        stream.revision <= pendingReply.value.revision
      )
        return;
      pendingReply.value = {
        ...pendingReply.value,
        content: (pendingReply.value.content ?? '') + stream.content,
        revision: stream.revision,
        updatedAt: new Date().toISOString(),
      };
      streamingContent.value = null;
    } else if (stream.kind === 'tool') {
      if (
        !pendingReply.value ||
        pendingReply.value.runId !== stream.runId ||
        stream.revision <= pendingReply.value.revision
      )
        return;
      const tools = [...(pendingReply.value.toolCalls ?? [])];
      // 旧 Worker 没有轮次标记时，工具开始也能识别下一轮正文；不提前清空正在阅读的内容。
      if (stream.tool.status === 'running') awaitingContentRound = true;
      const index = tools.findIndex((item) => item.id === stream.tool.id);
      if (index >= 0) tools[index] = stream.tool;
      else tools.push(stream.tool);
      pendingReply.value = {
        ...pendingReply.value,
        toolCalls: tools,
        revision: stream.revision,
        updatedAt: new Date().toISOString(),
      };
    } else if (stream.kind === 'completed') {
      if (stream.runId === stoppingRunId.value) stoppingRunId.value = null;
      if (pendingReply.value && pendingReply.value.runId !== stream.runId) return;
      finishRun(stream.runId);
      if (stream.errorCode === 'AI_CREDITS_INSUFFICIENT')
        window.dispatchEvent(
          new CustomEvent('ai-credits-insufficient', { detail: stream.errorDetails }),
        );
    } else if (stream.kind === 'context-compressed') {
      lastCompression.value = { summary: stream.summary };
      contextUsage.value = stream.contextUsage;
    }
  }

  return {
    agentId,
    workspaceId,
    sessionId,
    messages,
    conversationMessages,
    mentionCapabilities,
    userMessageContent,
    loadingHistory,
    replySyncError,
    suggestedRepliesMessageId,
    pendingReply: displayedPendingReply,
    pendingInterjections,
    resettableMessageIds,
    stoppingRunId,
    lastCompression,
    contextUsage,
    open,
    refresh,
    send,
    sendBrowserAnnotation,
    acknowledgeSentMessage,
    withdrawPendingInterjection,
    cancelActiveRun,
    close,
  };
});
